import {
  Component,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { IndicatorDataStore } from '../../../stores/indicatpr-data/indicator-data.state';
import { Indicator } from '../../../models/indicator';
import { IndicatorData } from '../../../models/indicator-data';
import { NgForOf } from '@angular/common';
import { DividerModule } from 'primeng/divider';
import { ButtonModule } from 'primeng/button';
import { DataLayerTypeStore } from '../../../stores/data-layer-type/data-layer-type.state';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { DataLayer } from '../../../models/data-layer';
import { DataLayerTypeNameComponent } from '../../atoms/data-layer-type-name/data-layer-type-name.component';
import { DataLayerType } from '../../../models/data-layer-type';
import { AlertComponent } from '../../atoms/alert/alert.component';
import { YearListComponent } from '../year-list/year-list.component';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'app-indicator-data-list',
  standalone: true,
  imports: [
    NgForOf,
    DividerModule,
    ButtonModule,
    DataLayerTypeNameComponent,
    AlertComponent,
    YearListComponent,
    RouterLink,
  ],
  templateUrl: './indicator-data-list.component.html',
  styleUrl: './indicator-data-list.component.scss',
})
export class IndicatorDataListComponent implements OnInit, OnChanges {
  readonly indicatorDataStore = inject(IndicatorDataStore);
  readonly dataLayerTypeStore = inject(DataLayerTypeStore);
  readonly dataLayerStore = inject(DataLayerStore);
  @Input() indicator!: Indicator;
  @Input() dataPublicationMode = false;
  @Input() disableImportButton = false;

  protected groupedData: Record<string, Record<string, IndicatorData>> | null =
    null;

  /**
   * Lifecycle hook that is called after Angular has initialized all data-bound properties of a component.
   * This method initializes the grouped data by calling the setGroupedData function.
   *
   * @return {void} This method does not return any value.
   */
  ngOnInit(): void {
    this.setGroupedData();
  }

  /**
   * Responds to changes in the input properties of the component.
   * This method is invoked whenever any data-bound input property changes.
   *
   * @param {SimpleChanges} changes - An object of current and previous property values containing changed inputs.
   * @return {void} This method does not return any value.
   */
  ngOnChanges(changes: SimpleChanges): void {
    this.setGroupedData();
  }

  /**
   * Updates the `groupedData` property by retrieving and setting data grouped by indicator type
   * from the `indicatorDataStore`.
   *
   * @return {void} Does not return a value.
   */
  setGroupedData(): void {
    this.groupedData = this.indicatorDataStore.getTypeGroupedByIndicator(
      this.indicator._id
    );
  }

  /**
   * Retrieves the type data, represented as an array of string keys, if groupedData exists.
   *
   * @return {string[] | undefined} An array of type keys if groupedData is present; otherwise, undefined.
   */
  get typeData(): string[] | undefined {
    if (!this.groupedData) return;
    return Object.keys(this.groupedData);
  }

  /**
   * Retrieves the data layers of a specific type based on the given identifier.
   *
   * @param {string} id - The identifier used to find corresponding data layers.
   * @return {DataLayer[] | undefined} An array of DataLayer objects if matched, or undefined if no match is found.
   */
  getTypeDataLayers(id: string): DataLayer[] | undefined {
    if (!this.groupedData || !this.groupedData[id]) return;
    const layerIdsWithData = Object.keys(this.groupedData[id]);
    return this.dataLayerStore.filterByParent(id).filter((layer: DataLayer) => {
      return layerIdsWithData.includes(layer._id);
    });
  }

  /**
   * Retrieves a DataLayerType object corresponding to the given ID.
   *
   * @param {string} id - The unique identifier of the type to retrieve.
   * @return {DataLayerType} The DataLayerType object associated with the specified ID.
   */
  getTypeById(id: string): DataLayerType {
    return this.dataLayerTypeStore.getById(id);
  }
}
